import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { get } from "lodash";

import { MediaType, keyframe } from "../shared";
import { getStoreLanguages } from "../../rise/hyperlive/utils";
import { DEFAULT_TRANSITION } from "../../rise/constants/transition";
import { Swoosh } from "../components/Icons";
import { HoiConfig } from "../components/ChapterRunner";
import { SnkrsCalDetail } from "../components/SnkrsCalDetail";
import SnkrsCalDropListEntry from "../components/SnkrsCalDropListEntry";
import SnkrsCalFooter from "../components/SnkrsCalFooter";
import SnkrsTransition from "../components/SnkrsTransition";
import { BG_COLOR, SnkrsCalMode, getDuration } from "../components/SnkrsCalHelpers";
import { useDerivedPlayMode } from "./SnkrsCal.hooks";

export interface SnkrsCalProps {
    drops: Array<DropInterface>;
    count?: number;
    play_range?: string;
    mode: SnkrsCalMode;
    index: number;
    hide_dates?: boolean;
    cta_label?: string;
    locale?: string;
    config: HoiConfig;
    available_copy?: string;
    qr_url?: string;
    play_mode: "grid" | "carousel";
    footer_copy?: string;
    concept?: "wof" | "one-nike";
    qr_large?: boolean;
}

export interface DropInterface {
    scc?: string;
    fulltitle: string;
    subtitle: string;
    slug: string;
    url?: string;
    key: string;
    colorDescription: string;
    description: string;
    startEntryDate: Date;
    images: Array<MediaType>;
    cover: MediaType;
    qr_link?: string;
    brand?: string;
    hidden?: boolean;
    hide_qr?: boolean;
    title?: string;
    date_label?: string;
    index?: number;
}

export function getPlayArray(play_range: string, absoluteLimit?: number) {
    const [start, end] = play_range.split(",").map(i => Number(i));
    const limited = absoluteLimit !== undefined ? Math.min(absoluteLimit - 1, end) : end;

    return [...new Array((limited - start) + 1)].map((a, i) => i + start);
}

export function calculateDuration(chapterData: SnkrsCalProps, config: HoiConfig) {
    return getDuration(chapterData, config);
}

export default function SnkrsCal(props: SnkrsCalProps) {
    const {
        drops,
        play_range = "0,5",
        count = 6,
        mode = "drops",
        index,
        hide_dates = false,
        cta_label,
        qr_url,
        locale: givenLocale,
        config,
        available_copy,
        play_mode = "grid",
        footer_copy,
        concept: forcedConcept,
        qr_large,
    } = props;

    const concept = useMemo(() => {
        const fromStore = get(window, "animation_store.json_data.store_type", "WOF")?.toLowerCase() === "wof" ? "wof" : "one-nike";
        return forcedConcept ?? fromStore;
    }, [forcedConcept]);

    const filtered = useMemo(() => {
        return drops?.slice(0, count);
    }, [drops, count]);

    const derivedPlayMode = useDerivedPlayMode(play_mode, filtered);

    const { snkrs } = config.duration;
    const locale = useMemo(() => {
        if (givenLocale) {
            return givenLocale;
        }

        return getStoreLanguages()?.[0] ?? "US";
    }, [givenLocale]);
    const [selected, setSelected] = useState(null);
    const [runner, setRunner] = useState(0);

    useEffect(() => {
        const { trigger, clear } = keyframe(0);
        const playLength = getPlayArray(play_range, drops.length);
        const modeDuration = ((mode === "drops" ? snkrs.drop : snkrs.story) / 1_000) + 1;
        const overview = derivedPlayMode === "carousel" ? 0 : snkrs.overview / 1_000;

        const retrigger = playLength.reduce((chainTrigger, currentIndex) => {
            return chainTrigger
                (overview, () => setSelected(currentIndex))
                (modeDuration, () => setSelected(null));
        }, trigger);

        retrigger(6, () => setRunner(i => i + 1));

        return clear;
    }, [runner, derivedPlayMode, play_range, drops.length, mode, snkrs]);

    useEffect(() => {
        if (derivedPlayMode === "carousel" && selected === null && filtered.length > 0) {
            setSelected(0);
        }
    }, [selected, derivedPlayMode, filtered.length]);

    return (
        <SnkrsTransition variant={"wipe-x"} style={{ position: "absolute" }}>
            <div
                style={{
                    background: concept === "wof" ? BG_COLOR : "#fff",
                    height: "100%",
                    width: "100%",
                }}
            >
                {concept === "one-nike" ? (
                    <div style={{ position: "absolute", top: 80, left: 40, zIndex: 99 }}>
                        <Swoosh color={"black"} />
                    </div>
                ) : null}

                <div
                    style={{
                        width: "100%",
                        height: mode === "stories" ? "100%" : "1708px",
                        display: "flex",
                        flexWrap: "wrap",
                    }}
                >
                    {derivedPlayMode === "grid" ? filtered.map((drop, currentIndex) => (
                        <SnkrsCalDropListEntry
                            locale={locale}
                            hide_dates={hide_dates}
                            index={index}
                            mode={mode}
                            key={drop.key}
                            drop={drop}
                            available_copy={available_copy}
                            onTop={selected === currentIndex}
                            concept={concept}
                        />
                    )) : null}
                </div>

                {concept === "wof" ? (
                    <SnkrsCalFooter
                        mode={mode}
                        footerCopy={footer_copy}
                    />
                ) : null}

                <AnimatePresence mode={"popLayout"}>
                    <motion.div
                        key={derivedPlayMode === "carousel" ? selected : undefined}
                        initial={{
                            x: derivedPlayMode === "carousel" ? "100%" : undefined,
                            opacity: 0,
                        }}
                        animate={{
                            x: derivedPlayMode === "carousel" ? "0%" : undefined,
                            opacity: 1,
                        }}
                        exit={{
                            x: derivedPlayMode === "carousel" ? "-100%" : undefined,
                            opacity: 0,
                        }}
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                        }}
                        transition={{
                            ...DEFAULT_TRANSITION,
                        }}
                    >
                        <SnkrsCalDetail
                            drop={filtered?.[selected]}
                            config={config}
                            mode={mode}
                            show={selected !== null}
                            index={index}
                            hide_dates={hide_dates}
                            cta_label={cta_label}
                            qr_url={qr_url}
                            locale={locale}
                            available_copy={available_copy}
                            play_mode={derivedPlayMode}
                            concept={concept}
                            qr_large={qr_large}
                        />
                    </motion.div>
                </AnimatePresence>
            </div>
        </SnkrsTransition>
    );
}
